import React, { useContext, useEffect,useState } from "react";
import { ContextEngine } from "../../engine/ContextEngine";
import { MODAL_TYPES } from "../../modals/modalTypes";
import { useSectionFolders } from "../../engine/hooks/useSectionFolders";
import { useFolderSheets } from "../../engine/hooks/useFolderSheets";
import { useNamespaces } from "../../engine/hooks/useNamespaces";
import { useSpaceContent } from "../../engine/hooks/useSpaceContent";

import {useTableSelection} from "../../engine/hooks/useTableSelection";
import "./CentralControlsBar1.css";
// ControlButton Component for actions like cancel, accept
const ControlButton = ({ control, icons, onClick }) => {
  // 1. Normalize the label (lowercase) to match ICON_MAP keys
  // 2. Remove trailing 's' if your map is singular, or add it if map is plural
  const lookupKey = control.label?.toLowerCase();
  
  // Try to find the icon, otherwise fallback to the string
  const content = icons && icons[lookupKey] ? icons[lookupKey] : control.label;

  return (
    <button
      style={{ 
        marginRight: "8px", 
        display: "inline-flex", 
        alignItems: "center", 
        justifyContent: "center",
        padding: "4px 8px", // Add a little padding for the emoji
        cursor: "pointer" 
      }}
      onClick={onClick}
      disabled={!control}
      title={control.label}
    >
      {content}
    </button>
  );
};



// ControlTextInput Component for single-line text input fields like selectedCell.value
const ControlTextInput = ({ control, onChange }) => (
  <div className="input-container">
    <label>{control.label}</label>
    <input
      type="text"
      value={control.value || ""}
      onChange={(e) => onChange(control.id, e.target.value)}
    />
  </div>
);

const ControlTextArea = ({ control, onFill, onChange, isDisabled, topControlHeight }) => {
  const [text, setText] = React.useState(control.value || "");

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); 
      if (!isDisabled && text.trim() !== "") {
        onFill(text); 
        setText(""); 
        onChange(control.id, ""); 
      }
    }
  };


  return (
    <div className="textarea-container" style={{ 
      height: `${topControlHeight}px`, // Use the full bar height
      display: 'flex', 
      flexDirection: 'column',
      /* CHANGE 1: Remove center and use flex-start (Top Align) */
      justifyContent: 'flex-start', 
      /* CHANGE 2: Remove all padding to stop the "glue" effect */
      padding: '2px 5px 5px 5px',
      margin: '0 5px', // Horizontal gap between controls only
      overflow: 'hidden' // Prevents any internal overflow from showing
    }}>
      <textarea
        disabled={isDisabled}
        placeholder={isDisabled ? "Select cells..." : "Type & Enter"}
        value={text}
        onKeyDown={handleKeyDown}
        onChange={(e) => {
          setText(e.target.value);
          onChange(control.id, e.target.value);
        }}
        style={{
          display: 'block',        // Removes inline-block bottom gap
          verticalAlign: 'top',    // Forces top alignment in older engines
          width: '100%',
          /* CHANGE 3: flex: 1 fills the container 100% top-to-bottom */
          flex: 1, 
          background: isDisabled ? '#111' : '#1a1a1a',
          color: 'white',
          border: '1px solid #333',
          /* CHANGE 4: Lower radius for a sharper "grid" look */
          borderRadius: '2px', 
          padding: '4px',
          resize: 'none',
          outline: 'none',
          fontSize: '12px',
          boxSizing: 'border-box' // Essential so padding doesn't cause overflow
        }}
      />
    </div>
  );
};

// ControlSelect Component for context-selector dropdowns
const ControlSelect = ({ control, items, activeItem, onChange, isLoading }) => (
  <div className="select-container">
    <select value={activeItem || ""} onChange={onChange}>
      {isLoading ? (
        <option>Loading...</option>
      ) : (
        <>
          {items.length === 0 && <option>No data available</option>}
          {items.map((item) => (
            <option value={item} key={item.row_id}>
              {item.cont_value || item.label || item.cont_name}
            </option>
          ))}
        </>
      )}
    </select>
  </div>
);


// ControlIconSelector Component for cells select with icons
const ControlIconSelector = ({ control, onChange }) => (
  <div className="icon-container">
    <label>{control.label}</label>
    <div className="icon-selector">
      {control.options.map((option, index) => (
        <button
          key={index}
          onClick={() => onChange(control.id, option)}
          style={{ background: `url(${option.icon})` }}
          className="icon-button"
        >
          <img src={option.icon} alt={option.label} />
        </button>
      ))}
    </div>
  </div>
);
const CentralControlsBar = () => {
  const { context, updateContext } = useContext(ContextEngine);
  const section = context.view?.selectedSection; // e.g., "build" or "user"
  const projectId = context.view?.selectedProject;
  const activeFolderId = context.view?.activeFolder;
  

  // --- BUILD MODE FETCHES ---
  const { data: adspaces = [] , isLoading: isLoadingSpaces} = useNamespaces(projectId, "build", {
    enabled: section === "build"
  });

  
  const { data: buildItems = [] , isLoading: isLoadingBuild} = useSpaceContent(activeFolderId, {
    enabled: section === "build" && !!activeFolderId // Fetch items for the Space
  });

  // --- USER MODE FETCHES ---
  const { data: userFolders = [], isLoading: isLoadingFolders } = useSectionFolders(projectId, "user", {
    enabled: section === "user"
  });

  const { data: userSheets = [],isLoading: isLoadingSheet } = useFolderSheets(projectId,activeFolderId, {
    enabled: section === "user" && !!activeFolderId
  });

  // // 2. CHOOSE THE SOURCE: Decide which data to pass to the dropdowns
  const folderItems = section === "build" ? adspaces : userFolders;
  const sheetItems = section === "build" ? buildItems : userSheets;


  // Retrieve the value of 'topControl' from the context
  const topControlHeight = context.view.topControl || 50; // default height if not defined
  // 1. Find the target table and its index in the global context
  const tableId = "defaultTable"; // or context.view.activeTable
  const unitIndex = context.context_units?.findIndex(u => u.id === tableId);
  const table = context.context_units?.[unitIndex];
  const columns = table.cvl || [];
  // Inside CentralControlsBar
  const allUnits = context.context_units || [];

  // Filter the units to get only the objects where type is "sheet"
  const sheets = allUnits.filter(u => u.type === "sheet");
  const folders = allUnits.filter(u => u.type === "folder");
  const { 
    view: { 
      activeSheet = "", 
      activeFolder = "", 
    } = {} 
  } = context;

    // 2. Identify selection for the "isDisabled" logic
  const selectedCells = table?.view?.selectedCells || [];
  // 3. Logic to determine if the controls should be disabled
  const isDisabled = selectedCells.length === 0 || selectedCells.some(cell => {
    const colDef = columns.find(c => c.name === cell.colName);
    return (
      colDef?.type === 'buttonList' || 
      colDef?.name === 'Seq' || 
      colDef?.locked === true
    );
  });

  const controls = context.controls || [];
  const openRowControlModal = (e, control) => {
    const selectedRowIds = table?.data
      ?.filter(item => item.meta?.selected === true)
      .map(item => item.row_id) || [];

    if (!selectedRowIds.length) {
      alert("Please select at least one row first!");
      return;
    }

    updateContext("view.modal", {
      type: "ROW_CONTROL",
      props: {
        rowIds: selectedRowIds,
        control,
        position: { x: e.clientX, y: e.clientY }
      }
    });
  };

  const SheetModal = (e, control) => {
    updateContext("view.modal", {
      type: "SHEET_SETTING",
    });
  };

  const leftControls = controls.filter(c => c.position == "left");
  const rightControls = controls.filter(c => c.position === "right");

  // Handle changes to control values
  const handleControlChange = (id, value) => {
    const updatedControls = controls.map((ctrl) =>
      ctrl.id === id ? { ...ctrl, value } : ctrl
    );
    updateContext("controls", updatedControls); // Update context with new control values
  };
  // 3. Update BOTH local state and context in your handler
const handleSheetChange = (e, manualId) => {
  try {
    // FIX: Get the value directly from e.target
    const value = e.target.value; 
    
    // Use manualId if provided, otherwise fallback to e.target.id
    // Note: Ensure your ControlSelect <select> has an id prop passed to it!
    const id = manualId || e.target.id;      
    
    const key = id.includes("sheet") ? "activeSheet" : "activeFolder";
    
    console.log("WE ARE HERE:", value, "Key:", key);      
    
    updateContext(`view.${key}`, value);
  } catch (err) {
    console.error("Selection error:", err);
  }
};

  // 2. In CentralControlsBar, get the function from the hook
const { handleBulkFillAction } = useTableSelection({
  data: table?.data || [],
  columns: table?.cvl || [],
  updateContext,
  unitIndex, // Ensure you find the index of the active table
  selectedCells: table?.view?.selectedCells || []
});

  const ICON_MAP = {
    namespaces: "💎",
    sheets: "📊",
    events: "⚡",
    controls: "🕹️",
    agents: "🤖",
    actions: "🚀", // Added a fallback for actions
    link: "🚀",
    setting: "⚙️", // Added a fallback for actions
    plus: "➕",      // or ➕ (Heavy Plus Sign)
    up: "⬆️",
  };

useEffect(() => {
  // 1. Only run if we have options and we aren't currently loading
  const folderOptions = section === "build" ? adspaces : userFolders;
  const sheetOptions = section === "build" ? buildItems : userSheets;

  // AUTO-SELECT FOLDER
  if (folderOptions.length > 0) {
    const isValid = folderOptions.some(opt => opt.row_id === activeFolder);
    if (!isValid) {
      const firstId = folderOptions[0].row_id;
      updateContext("view.activeFolder", firstId);
      // Reset sheet too, since the folder changed
      updateContext("view.activeSheet", null); 
    }
  }

  // AUTO-SELECT SHEET
  if (sheetOptions.length > 0) {
    const isValid = sheetOptions.some(opt => opt.row_id === activeSheet);
    if (!isValid) {
      const firstId = sheetOptions[0].row_id;
      updateContext("view.activeSheet", firstId);
    }
  }
// Watch these variables for changes
}, [adspaces, buildItems, userFolders, userSheets, activeFolder, activeSheet, section]);

  return (
    <div 
      className="central-controls-bar"
      style={{ 
        height: `${topControlHeight}px`,
        maxHeight: `${topControlHeight}px`, // Hard limit
        minHeight: `${topControlHeight}px`, // Hard limit
        display: 'flex', 
        alignItems: 'flex-start', 
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '2px 10px' 
      }}
    >
      {/* Left Controls (in one row) */}
      <div className="controls-left" style={{ display: 'flex', alignItems: 'flex-start' }}>
        <span className="left-controls-group" style={{ display: 'flex', alignItems: 'flex-start' }}>
          {leftControls.map((ctrl) => {
            switch (ctrl.type) {
              case "context_selector":
                return (
                <ControlButton
                    key={ctrl.id}
                    control={ctrl}
                    isons = {ICON_MAP}
                    onClick={(e) => openRowControlModal(e, ctrl)}
                  />
                );
              case "select": {
                const isSheet = ctrl.id.includes("sheet");
                const isFolder = ctrl.id.includes("folder");
                // 1. If it's neither, don't render anything (or render a default)
                if (!isSheet && !isFolder) return null;
                // 2. Select the correct data and state based on the ID
                const options = isSheet ? sheetItems : folderItems;
                const activeItem = isSheet ? activeSheet : activeFolder;
                console.log("WEAREINSTART",options);
                // FIX: Correctly check the loading state based on Section + Type
                const currentLoading = isSheet 
                  ? (section === "build" ? isLoadingBuild : isLoadingSheet) 
                  : (section === "build" ? isLoadingSpaces : isLoadingFolders); // <--- Use Folders for User mode


                // 3. Return a single component instance
                return (
                  <ControlSelect
                    key={ctrl.id}
                    control={ctrl}
                    items={options} // You might want to rename this prop to 'options' in the component later!
                    activeItem={activeItem}
                    onChange={(e) => handleSheetChange(e, ctrl.id)} 
                    isLoading={currentLoading}
                  />
                );
              }                
              case "input":
                return (
                  <ControlTextInput
                    key={ctrl.id}
                    control={ctrl}
                    onChange={handleControlChange}
                  />
                );
              case "textarea":
                return (
                <ControlTextArea
                  key={ctrl.id}
                  control={ctrl}
                  isDisabled={isDisabled}      // Pass the variable defined in CentralControlsBar
                  topControlHeight={topControlHeight} // <--- Pass it here
                  onChange={handleControlChange} // Pass the local handler
                  onFill={handleBulkFillAction}  // Pass the hook function
                />
                );
              case "action":
                return (
                  <ControlButton
                    key={ctrl.id}
                    control={ctrl}
                    icons = {ICON_MAP}
                    onClick={(e) => SheetModal(e, ctrl)}
                  />
                );
              case "cells":
                return (
                  <ControlIconSelector
                    key={ctrl.id}
                    control={ctrl}
                    onChange={handleControlChange}
                  />
                );
              default:
              return (
                <ControlButton
                  key={ctrl.id}
                  control={ctrl}
                  icons = {ICON_MAP}
                  onClick={(e) => openRowControlModal(e, ctrl)}
                />
              );
            }
          })}
        </span>
      </div>

      {/* Central Controls (Fill remaining space) */}
      <div className="controls-central">
        {controls
          .filter((ctrl) => ctrl.position === "fill")
          .map((ctrl) => {
            switch (ctrl.type) {
              case "textarea":
                return (
                <ControlTextArea
                  key={ctrl.id}
                  control={ctrl}
                  isDisabled={isDisabled}      // Pass the variable defined in CentralControlsBar
                  topControlHeight={topControlHeight} // <--- Pass it here
                  onChange={handleControlChange} // Pass the local handler
                  onFill={handleBulkFillAction}  // Pass the hook function
                />
                );

              default:
                return null;
            }
          })}
      </div>

      {/* Right Controls */}
      <div className="controls-right">
        {rightControls.map((ctrl) => {
          switch (ctrl.type) {
            case "context_selector":
              return (
               <ControlButton
                  key={ctrl.id}
                  control={ctrl}
                  icons = {ICON_MAP}
                  onClick={(e) => openRowControlModal(e, ctrl)}
                />
              );
            case "action":
              return (
                <ControlButton
                  key={ctrl.id}
                  control={ctrl}
                  icons = {ICON_MAP}
                  onClick={(e) => SheetModal(e, ctrl)}
                />
              );
            default:
              return (
                <ControlButton
                  key={ctrl.id}
                  control={ctrl}
                  icons = {ICON_MAP}
                  onClick={(e) => openRowControlModal(e, ctrl)}
                />
              );
          }
        })}
      </div>
    </div>
  );
};

export default CentralControlsBar;
